import React from 'react';
import { Activity, TrendingUp, ClipboardList, Award, LucideIcon } from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Categorical palette (validated for CVD-safe adjacent pairs — see dataviz skill)
const COLOR_BLUE = '#2a78d6';
const COLOR_ORANGE = '#eb6834';
const COLOR_AQUA = '#1baf7a';
const COLOR_YELLOW = '#eda100';

const TEXT_SECONDARY = '#52514e';
const TEXT_MUTED = '#898781';
const GRIDLINE = '#eef0f3';

export interface AttendanceTrendPoint {
  date: string;
  count: number;
}

export interface VisitsTrendPoint {
  month: string;
  doctor_visits: number;
  firm_visits: number;
}

interface ChartCardProps {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  accent: string;
  headerRight?: React.ReactNode;
  isLoading: boolean;
  isEmpty?: boolean;
  height?: number;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  icon: Icon,
  iconColor,
  iconBg,
  accent,
  headerRight,
  isLoading,
  isEmpty,
  height = 256,
  children,
}) => (
  <div className="relative bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 p-6 overflow-hidden h-full">
    <div className="absolute inset-x-0 top-0 h-1" style={{ background: accent }} />
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`p-2.5 rounded-xl ${iconBg} flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
      {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
    </div>
    {isLoading ? (
      <div className="rounded-xl bg-gray-100 animate-pulse" style={{ height }} />
    ) : isEmpty ? (
      <div className="flex flex-col items-center justify-center gap-2 text-gray-300" style={{ height }}>
        <Icon className="w-9 h-9" />
        <p className="text-sm text-gray-400">No data yet</p>
      </div>
    ) : (
      children
    )}
  </div>
);

const Badge: React.FC<{ value: React.ReactNode; label: string; bg: string; text: string }> = ({ value, label, bg, text }) => (
  <span className={`inline-flex items-baseline gap-1 px-3 py-1.5 rounded-full ${bg} ${text}`}>
    <span className="text-sm font-bold leading-none">{value}</span>
    <span className="text-[11px] font-medium leading-none opacity-80">{label}</span>
  </span>
);

const tooltipContentStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #eef0f3',
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(11,11,11,0.10)',
  color: '#0b0b0b',
  fontSize: 13,
  padding: '8px 12px',
};

const tooltipLabelStyle: React.CSSProperties = { color: TEXT_SECONDARY, fontWeight: 600, marginBottom: 4 };

const formatDateLabel = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' });

/** Reserve enough YAxis width for the widest tick label — a fixed 28px clips 4+ digit values. */
const yAxisWidthFor = (maxValue: number) => Math.max(28, String(Math.round(maxValue)).length * 8 + 12);

interface AttendanceTrendChartProps {
  data: AttendanceTrendPoint[];
  isLoading: boolean;
}

export const AttendanceTrendChart: React.FC<AttendanceTrendChartProps> = ({ data, isLoading }) => {
  const isEmpty = !isLoading && data.every(d => d.count === 0);
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const yAxisWidth = yAxisWidthFor(Math.max(0, ...data.map((d) => d.count)));

  return (
    <ChartCard
      title="Attendance Trend"
      subtitle="Employees signed in — last 7 days"
      icon={Activity}
      iconColor="text-blue-600"
      iconBg="bg-blue-50"
      accent={COLOR_BLUE}
      isLoading={isLoading}
      isEmpty={isEmpty}
      headerRight={!isLoading && !isEmpty && <Badge value={total} label="check-ins" bg="bg-blue-50" text="text-blue-700" />}
    >
      <ResponsiveContainer width="100%" height={256}>
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="attendanceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLOR_BLUE} stopOpacity={0.28} />
              <stop offset="100%" stopColor={COLOR_BLUE} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={GRIDLINE} strokeWidth={1} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateLabel}
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            axisLine={{ stroke: GRIDLINE }}
            tickLine={false}
            tickMargin={8}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={yAxisWidth}
          />
          <Tooltip
            contentStyle={tooltipContentStyle}
            labelStyle={tooltipLabelStyle}
            cursor={{ stroke: GRIDLINE, strokeWidth: 1 }}
            labelFormatter={(v) => formatDateLabel(String(v))}
            formatter={(value: any) => [`${value} employees`, 'Signed in']}
          />
          <Area
            type="monotone"
            dataKey="count"
            name="Signed in"
            stroke={COLOR_BLUE}
            strokeWidth={2.5}
            strokeLinecap="round"
            fill="url(#attendanceFill)"
            isAnimationActive={false}
            dot={{ r: 4, stroke: '#ffffff', strokeWidth: 2, fill: COLOR_BLUE }}
            activeDot={{ r: 6, stroke: '#ffffff', strokeWidth: 2, fill: COLOR_BLUE }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

interface VisitsTrendChartProps {
  data: VisitsTrendPoint[];
  isLoading: boolean;
}

export const VisitsTrendChart: React.FC<VisitsTrendChartProps> = ({ data, isLoading }) => {
  const isEmpty = !isLoading && data.every(d => d.doctor_visits === 0 && d.firm_visits === 0);
  const total = data.reduce((sum, d) => sum + d.doctor_visits + d.firm_visits, 0);
  const yAxisWidth = yAxisWidthFor(Math.max(0, ...data.flatMap((d) => [d.doctor_visits, d.firm_visits])));

  return (
    <ChartCard
      title="Visits Trend"
      subtitle="Doctor & firm visits — last 6 months"
      icon={TrendingUp}
      iconColor="text-orange-600"
      iconBg="bg-orange-50"
      accent={`linear-gradient(90deg, ${COLOR_BLUE}, ${COLOR_ORANGE})`}
      isLoading={isLoading}
      isEmpty={isEmpty}
      headerRight={!isLoading && !isEmpty && <Badge value={total} label="total visits" bg="bg-orange-50" text="text-orange-700" />}
    >
      <ResponsiveContainer width="100%" height={256}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4} barCategoryGap="28%">
          <CartesianGrid vertical={false} stroke={GRIDLINE} strokeWidth={1} />
          <XAxis
            dataKey="month"
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            axisLine={{ stroke: GRIDLINE }}
            tickLine={false}
            tickMargin={8}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={yAxisWidth}
          />
          <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={{ fill: 'rgba(11,11,11,0.03)' }} />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 13, color: TEXT_SECONDARY, paddingTop: 12 }}
          />
          <Bar dataKey="doctor_visits" name="Doctor visits" fill={COLOR_BLUE} radius={[4, 4, 0, 0]} maxBarSize={20} isAnimationActive={false} />
          <Bar dataKey="firm_visits" name="Firm visits" fill={COLOR_ORANGE} radius={[4, 4, 0, 0]} maxBarSize={20} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

interface PendingBreakdownChartProps {
  pendingLeaves: number;
  pendingTourPlans: number;
  pendingDeviations: number;
  pendingReschedules: number;
  isLoading: boolean;
}

export const PendingBreakdownChart: React.FC<PendingBreakdownChartProps> = ({
  pendingLeaves,
  pendingTourPlans,
  pendingDeviations,
  pendingReschedules,
  isLoading,
}) => {
  const data = [
    { name: 'Leaves', value: pendingLeaves, color: COLOR_BLUE },
    { name: 'Tour Plans', value: pendingTourPlans, color: COLOR_ORANGE },
    { name: 'Deviations', value: pendingDeviations, color: COLOR_AQUA },
    { name: 'Reschedules', value: pendingReschedules, color: COLOR_YELLOW },
  ];
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const isEmpty = !isLoading && total === 0;

  return (
    <ChartCard
      title="Pending Approvals"
      subtitle="Breakdown by request type"
      icon={ClipboardList}
      iconColor="text-indigo-600"
      iconBg="bg-indigo-50"
      accent={`linear-gradient(90deg, ${COLOR_BLUE}, ${COLOR_ORANGE}, ${COLOR_AQUA}, ${COLOR_YELLOW})`}
      isLoading={isLoading}
      isEmpty={isEmpty}
      height={300}
    >
      <div className="relative">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Tooltip
              contentStyle={tooltipContentStyle}
              labelStyle={tooltipLabelStyle}
              formatter={(value: any, name: any) => [`${value} pending`, name]}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={62}
              outerRadius={88}
              paddingAngle={3}
              startAngle={90}
              endAngle={-270}
              isAnimationActive={false}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} stroke="#ffffff" strokeWidth={2} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-bold text-gray-900 leading-none">{total}</span>
          <span className="text-xs text-gray-500 mt-1">Pending</span>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2.5">
        {data.map((d) => (
          <div key={d.name} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
              <span className="text-xs text-gray-600 truncate">{d.name}</span>
            </div>
            <span className="text-xs font-semibold text-gray-900 flex-shrink-0">{d.value}</span>
          </div>
        ))}
      </div>
    </ChartCard>
  );
};

export interface EmployeePerformancePoint {
  employee_id: number;
  name: string;
  designation?: string | null;
  doctor_visit_count: number;
  firm_visit_count: number;
  total_call_count: number;
}

const RANK_STYLES = [
  { bg: 'bg-yellow-100', text: 'text-yellow-700' }, // gold
  { bg: 'bg-gray-200', text: 'text-gray-600' },     // silver
  { bg: 'bg-orange-100', text: 'text-orange-700' }, // bronze
];

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

interface EmployeePerformanceListProps {
  data: EmployeePerformancePoint[];
  isLoading: boolean;
}

export const EmployeePerformanceList: React.FC<EmployeePerformanceListProps> = ({ data, isLoading }) => {
  const isEmpty = !isLoading && data.length === 0;
  const maxCalls = Math.max(1, ...data.map((d) => d.total_call_count));

  return (
    <ChartCard
      title="Last 30 Days Performance"
      subtitle={`All field representatives${data.length ? ` (${data.length})` : ''}`}
      icon={Award}
      iconColor="text-yellow-600"
      iconBg="bg-yellow-50"
      accent={COLOR_YELLOW}
      isLoading={isLoading}
      isEmpty={isEmpty}
      height={320}
    >
      <div className="max-h-[560px] overflow-y-auto pr-1 space-y-4">
        {data.map((row, i) => {
          const rankStyle = RANK_STYLES[i] ?? { bg: 'bg-gray-100', text: 'text-gray-500' };
          const pct = (row.total_call_count / maxCalls) * 100;
          return (
            <div key={row.employee_id} className="flex items-center gap-3">
              <span
                className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${rankStyle.bg} ${rankStyle.text}`}
              >
                {i + 1}
              </span>
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold flex-shrink-0">
                {getInitials(row.name)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate">{row.name}</span>
                    {row.designation && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 flex-shrink-0">
                        {row.designation}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-gray-900 flex-shrink-0">{row.total_call_count} calls</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${pct}%`, background: COLOR_YELLOW }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {row.doctor_visit_count} doctor {row.doctor_visit_count === 1 ? 'visit' : 'visits'} · {row.firm_visit_count} firm {row.firm_visit_count === 1 ? 'visit' : 'visits'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
};
